import type { LC } from '@use-gpu/live';
import { use, wrap, useOne } from '@use-gpu/live';

import { Compute } from './compute';
import { Readback } from './readback';
import { useRenderContext } from '../providers/render-provider';

export type ScreenshotProps = {
  once?: boolean,
  type?: string,
  onBlob?: (blob: Blob) => void,
  onDataURL?: (dataUrl: string) => void,
};

export const Screenshot: LC<ScreenshotProps> = (props: ScreenshotProps) => {
  const {once, type, onBlob, onDataURL} = props;

  const renderContext = useRenderContext();
  const {source} = renderContext;
  if (!source) {
    throw new Error("Render context lacks a source.");
  }

  const canvas = useOne(() => document.createElement('canvas'));

  return (
    wrap(Compute,
      use(Readback, {
        once,
        source,
        then: (data: /*Uint8Array | Float16Array*/ any) => {
          const {
            size: [width, height]
          } = source;

          canvas.width = width;
          canvas.height = height;

          const array = (data instanceof Uint8Array ? new Uint8ClampedArray(data.buffer) : data);
          if (!array) {
            throw new Error(`Cannot screenshot format '${source.format}'. Use 'rgba8unorm', 'bgra8unorm' or 'rgba16float'.`);
          }

          // Swap from BGRA to RGBA if needed
          if (source.format === 'bgra8unorm') {
            const n = width * height;
            for (let i = 0; i < n; ++i) {
              const i4 = i * 4;
              const b = array[i4];
              const r = array[i4 + 2];
              array[i4] = r;
              array[i4 + 2] = b;
            }
          }

          const pixelFormat = source.format === 'rgba16float' ? 'rgba-float16' : 'rgba-unorm8';

          const imageData = new ImageData(array as any, width, height, {
            pixelFormat,
            colorSpace: 'srgb',
          } as any); // TODO: new ImageData API

          const context = canvas.getContext('2d');
          if (context) {
            context.putImageData(imageData, 0, 0);

            if (onBlob) {
              canvas.toBlob((blob) => {
                if (blob) onBlob(blob);
                else throw new Error("Error creating image blob.");
              }, type, 1);
            }

            if (onDataURL) {
              onDataURL(canvas.toDataURL(type, 1));
            }
          }

          return null;
        },
      })
    )
  );
};
